/**
 * Purpose-built PDF report renderer (jsPDF + autotable).
 * Charts are embedded as high-res PNG data URLs from the canvas pipeline.
 */
import type { SupportedLocale } from "@repo/core";
import type { AnnualReportPayload } from "./buildReportPayload";
import { createPdfTranslator, monthShort, type PdfTranslate } from "./pdfCopy";
import {
  addChartImage,
  addImageSafe,
  addTable,
  applyChrome,
  bodyText,
  coverPage,
  createReportDoc,
  downloadPdfBlob,
  PDF_MARGIN,
  sectionTitle,
  summaryMoney,
  tableMoney,
  type PdfDoc,
} from "./pdfLayout";
import { readStoredLocale } from "../../i18n/translations";
import { personLabel } from "../../i18n/labels";

export interface ReportChartImages {
  cashFlow?: string | null;
  rule503020?: string | null;
  netWorth?: string | null;
}

function resolveUiLocale(payloadLocale?: string, override?: SupportedLocale): SupportedLocale {
  if (override) return override;
  // Currency/Intl locale (en-US) ≠ UI locale (en|es|…); prefer stored app language.
  void payloadLocale;
  return readStoredLocale();
}

function fmtDate(iso: string, locale: string): string {
  try {
    return new Intl.DateTimeFormat(locale, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}

function renderDashboard(doc: PdfDoc, y: number, p: AnnualReportPayload, t: PdfTranslate): number {
  y = sectionTitle(doc, y, t("pdf.section.yearGlance"), 40);
  const { currency, locale } = p;
  const rows = p.kpis.map((k) => [t(`pdf.kpi.${k.key}`), k.display]);
  y = addTable(doc, y, [t("pdf.col.kpi"), t("pdf.col.value")], rows, {
    columnStyles: { 1: { halign: "right" } },
  });
  y = bodyText(
    doc,
    y,
    t("pdf.savingsGoal", {
      actual: summaryMoney(p.dashboard.actualSaved, currency, locale),
      target: summaryMoney(p.dashboard.totalSavedTarget, currency, locale),
    }),
    9
  );
  return y + 2;
}

function renderCashFlow(
  doc: PdfDoc,
  y: number,
  p: AnnualReportPayload,
  t: PdfTranslate,
  chart?: string | null
): number {
  y = sectionTitle(doc, y, t("pdf.section.cashFlow"), chart ? 96 : 40);
  y = addChartImage(doc, y, chart);
  const { currency, locale } = p;
  const body = p.monthlyCashFlow.map((m) => [
    monthShort(m.month, locale, p.year),
    tableMoney(m.income, currency, locale),
    tableMoney(m.expense, currency, locale),
    tableMoney(m.leftover, currency, locale),
  ]);
  return addTable(
    doc,
    y,
    [t("pdf.col.month"), t("pdf.col.in"), t("pdf.col.out"), t("pdf.col.leftover")],
    body,
    { columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } } }
  );
}

function renderBudget(doc: PdfDoc, y: number, p: AnnualReportPayload, t: PdfTranslate): number {
  const { currency, locale } = p;
  const label = monthShort(p.budget.month, locale, p.year);
  y = sectionTitle(
    doc,
    y,
    t("pdf.section.budget", { month: label, year: p.year }),
    44
  );

  y = bodyText(doc, y, t("pdf.sub.incomeSources"), 10);
  y = addTable(
    doc,
    y,
    [t("pdf.col.category"), t("pdf.col.target"), t("pdf.col.actual"), t("pdf.col.variance")],
    p.budget.income.map((r) => [
      r.name,
      tableMoney(r.target, currency, locale),
      tableMoney(r.actual, currency, locale),
      tableMoney(r.variance, currency, locale),
    ]),
    { columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } } }
  );

  y = bodyText(doc, y, t("pdf.sub.nws"), 10);
  y = addTable(
    doc,
    y,
    [t("pdf.col.group"), t("pdf.col.target"), t("pdf.col.actual")],
    p.budget.groups.map((g) => [
      t(`group.${g.group}`),
      tableMoney(g.target, currency, locale),
      tableMoney(g.actual, currency, locale),
    ]),
    { columnStyles: { 1: { halign: "right" }, 2: { halign: "right" } } }
  );

  y = bodyText(doc, y, t("pdf.sub.expenseCats"), 10);
  return addTable(
    doc,
    y,
    [t("pdf.col.category"), t("pdf.col.group"), t("pdf.col.target"), t("pdf.col.actual")],
    p.budget.expense.map((r) => [
      r.name,
      t(`group.${r.group}`),
      tableMoney(r.target, currency, locale),
      tableMoney(r.actual, currency, locale),
    ]),
    { columnStyles: { 2: { halign: "right" }, 3: { halign: "right" } } }
  );
}

function renderRule(
  doc: PdfDoc,
  y: number,
  p: AnnualReportPayload,
  t: PdfTranslate,
  chart?: string | null
): number {
  const r = p.rule503020;
  const { currency, locale } = p;
  y = sectionTitle(doc, y, t("pdf.section.rule"), chart ? 80 : 44);
  if (chart) {
    y = addImageSafe(doc, y, chart, 72);
  } else {
    y = bodyText(doc, y, t("pdf.empty.chart"), 9);
  }
  const hasData = r.needs + r.wants + r.savings > 0;
  if (!hasData) {
    y = bodyText(doc, y, t("pdf.empty.rule"), 9);
  }
  return addTable(
    doc,
    y,
    [t("pdf.col.bucket"), t("pdf.col.amount"), t("pdf.col.pctIncome"), t("pdf.col.targetPct")],
    [
      [t("group.needs"), tableMoney(r.needs, currency, locale), `${r.needsPct.toFixed(1)}%`, "50%"],
      [t("group.wants"), tableMoney(r.wants, currency, locale), `${r.wantsPct.toFixed(1)}%`, "30%"],
      [t("group.savings"), tableMoney(r.savings, currency, locale), `${r.savingsPct.toFixed(1)}%`, "20%"],
      [t("group.income"), tableMoney(r.income, currency, locale), "100%", "—"],
    ],
    { columnStyles: { 1: { halign: "right" }, 2: { halign: "right" }, 3: { halign: "right" } } }
  );
}

function renderDebts(doc: PdfDoc, y: number, p: AnnualReportPayload, t: PdfTranslate): number {
  const { currency, locale } = p;
  y = sectionTitle(doc, y, t("pdf.section.debts"), 42);
  y = bodyText(
    doc,
    y,
    t("pdf.debtSummary", {
      debt: summaryMoney(p.debts.totalBalance, currency, locale),
      interest: summaryMoney(p.debts.totalInterest, currency, locale),
      date: p.debts.debtFreeDateIso ? fmtDate(p.debts.debtFreeDateIso, locale) : "—",
    }),
    9
  );
  if (!p.debts.rows.length) {
    return bodyText(doc, y, t("pdf.empty.debts"), 9);
  }
  return addTable(
    doc,
    y,
    [
      "#",
      t("pdf.col.debt"),
      t("pdf.col.balance"),
      t("pdf.col.apr"),
      t("pdf.col.interest"),
      t("pdf.col.months"),
      t("pdf.col.payoff"),
    ],
    p.debts.rows.map((r) => [
      r.order,
      r.name,
      tableMoney(r.balance, currency, locale),
      `${r.interestRate}%`,
      tableMoney(r.totalInterest, currency, locale),
      r.monthsToPayoff,
      fmtDate(r.payoffDateIso, locale),
    ]),
    {
      columnStyles: {
        0: {halign: "center" },
        2: {halign: "right" },
        4: {halign: "right" },
        5: {halign: "right" },
      },
    }
  );
}

function renderFunds(doc: PdfDoc, y: number, p: AnnualReportPayload, t: PdfTranslate): number {
  const { currency, locale } = p;
  y = sectionTitle(doc, y, t("pdf.section.funds"), 40);
  if (!p.sinkingFunds.length) {
    return bodyText(doc, y, t("pdf.empty.funds"), 9);
  }
  return addTable(
    doc,
    y,
    [
      t("pdf.col.fund"),
      t("pdf.col.saved"),
      t("pdf.col.target"),
      t("pdf.col.progress"),
      t("pdf.col.moNeeded"),
    ],
    p.sinkingFunds.map((f) => [
      f.name,
      tableMoney(f.currentSaved, currency, locale),
      tableMoney(f.targetAmount, currency, locale),
      `${f.pct.toFixed(0)}%`,
      tableMoney(f.monthlyNeeded, currency, locale),
    ]),
    {
      columnStyles: {
        1: {halign: "right" },
        2: {halign: "right" },
        3: {halign: "right" },
        4: {halign: "right" },
      },
    }
  );
}

function renderNetWorth(
  doc: PdfDoc,
  y: number,
  p: AnnualReportPayload,
  t: PdfTranslate,
  chart?: string | null
): number {
  const { currency, locale } = p;
  const s = p.netWorth.stats;
  y = sectionTitle(doc, y, t("pdf.section.netWorth"), chart ? 96 : 40);
  y = bodyText(
    doc,
    y,
    t("pdf.nwSummary", {
      net: summaryMoney(s.netWorth, currency, locale),
      assets: summaryMoney(s.assets, currency, locale),
      debts: summaryMoney(s.debts, currency, locale),
    }),
    9
  );
  y = addChartImage(doc, y, chart);
  if (p.netWorth.logs.length) {
    y = addTable(
      doc,
      y,
      [t("pdf.col.month"), t("pdf.col.assets"), t("pdf.col.debts"), t("pdf.col.net")],
      p.netWorth.logs.map((l) => [
        l.monthYear,
        tableMoney(l.assets, currency, locale),
        tableMoney(l.debts, currency, locale),
        tableMoney(l.net, currency, locale),
      ]),
      { columnStyles: { 1: {halign: "right" }, 2: {halign: "right" }, 3: {halign: "right" } } }
    );
  }
  if (p.netWorth.accounts.length) {
    y = bodyText(doc, y, t("pdf.sub.accounts"), 10);
    y = addTable(
      doc,
      y,
      [t("pdf.col.account"), t("pdf.col.type"), t("pdf.col.balance")],
      p.netWorth.accounts.map((a) => [
        a.name,
        t(`accountType.${a.type}`),
        tableMoney(a.balance, currency, locale),
      ]),
      { columnStyles: { 2: {halign: "right" } } }
    );
  }
  return y;
}

export interface PdfRenderOptions {
  /** App UI locale for static strings (en|es|…). Defaults to stored locale. */
  uiLocale?: SupportedLocale;
}

/** Build full annual PDF and trigger download. */
export function downloadAnnualReportPdf(
  payload: AnnualReportPayload,
  charts: ReportChartImages = {},
  filename?: string,
  opts?: PdfRenderOptions
): void {
  const uiLocale = resolveUiLocale(payload.locale, opts?.uiLocale);
  const t = createPdfTranslator(uiLocale);
  const doc = createReportDoc();
  const generatedLabel = t("pdf.generated", {
    date: fmtDate(payload.generatedAtIso, payload.locale),
  });
  const monthLabel = monthShort(payload.month ?? 1, uiLocale, payload.year);
  const title = t("pdf.title.annual", { year: payload.year });
  const periodLabel = t("pdf.period.detail", { year: payload.year, month: monthLabel });

  coverPage(doc, {
    brand: payload.brand,
    title,
    subtitle: periodLabel,
    generatedLabel,
    tagline: t("pdf.tagline"),
    disclaimer: t("pdf.cover.disclaimer"),
    viewLabel:
      payload.person && payload.person !== "All"
        ? t("pdf.view", { person: personLabel(payload.person, t) })
        : undefined,
  });

  let y = PDF_MARGIN + 16;
  y = renderDashboard(doc, y, payload, t);
  y = renderCashFlow(doc, y, payload, t, charts.cashFlow);
  y = renderBudget(doc, y, payload, t);
  y = renderRule(doc, y, payload, t, charts.rule503020);
  y = renderDebts(doc, y, payload, t);
  y = renderFunds(doc, y, payload, t);
  renderNetWorth(doc, y, payload, t, charts.netWorth);

  applyChrome(doc, {
    brand: payload.brand,
    periodLabel,
    generatedLabel,
    pageLabel: (n, total) => t("pdf.page", { n, total }),
  });

  downloadPdfBlob(doc, filename ?? `BudgetOS-${payload.year}-Annual-Report.pdf`);
}

/** Monthly budget quick export — same layout primitives. */
export function downloadMonthlyBudgetPdf(
  payload: AnnualReportPayload,
  filename?: string,
  opts?: PdfRenderOptions
): void {
  const uiLocale = resolveUiLocale(payload.locale, opts?.uiLocale);
  const t = createPdfTranslator(uiLocale);
  const doc = createReportDoc();
  const generatedLabel = t("pdf.generated", {
    date: fmtDate(payload.generatedAtIso, payload.locale),
  });
  const monthLabel = monthShort(payload.budget.month, uiLocale, payload.year);
  const title = t("pdf.section.budget", { month: monthLabel, year: payload.year });

  coverPage(doc, {
    brand: payload.brand,
    title,
    subtitle: periodLabelish(payload, t, uiLocale),
    generatedLabel,
    tagline: t("pdf.tagline"),
    disclaimer: t("pdf.cover.disclaimer"),
    viewLabel:
      payload.person && payload.person !== "All"
        ? t("pdf.view", { person: personLabel(payload.person, t) })
        : undefined,
  });

  renderBudget(doc, PDF_MARGIN + 16, payload, t);
  applyChrome(doc, {
    brand: payload.brand,
    periodLabel: title,
    generatedLabel,
    pageLabel: (n, total) => t("pdf.page", { n, total }),
  });
  downloadPdfBlob(
    doc,
    filename ??
      `BudgetOS-Monthly-${payload.year}-${String(payload.budget.month).padStart(2, "0")}.pdf`
  );
}

function periodLabelish(
  payload: AnnualReportPayload,
  t: PdfTranslate,
  uiLocale: SupportedLocale
): string {
  const monthLabel = monthShort(payload.month ?? payload.budget.month, uiLocale, payload.year);
  return t("pdf.period.detail", { year: payload.year, month: monthLabel });
}

/** Per-debt amortization schedule export. */
export function downloadDebtAmortizationPdf(opts: {
  brand?: string;
  debtName: string;
  currency: string;
  locale: string;
  schedule: {
    month: number;
    payment: number;
    interest: number;
    principal: number;
    balance: number;
  }[];
  totalInterest: number;
  payoffDateIso: string;
  filename?: string;
  uiLocale?: SupportedLocale;
}): void {
  const uiLocale = resolveUiLocale(opts.locale, opts.uiLocale);
  const t = createPdfTranslator(uiLocale);
  const doc = createReportDoc();
  const brand = opts.brand ?? "BudgetOS";
  const generatedLabel = t("pdf.generated", {
    date: fmtDate(new Date().toISOString(), opts.locale),
  });
  const title = t("pdf.amortization", { name: opts.debtName });

  coverPage(doc, {
    brand,
    title,
    subtitle: t("pdf.payoffInterest", {
      date: fmtDate(opts.payoffDateIso, opts.locale),
      interest: summaryMoney(opts.totalInterest, opts.currency, opts.locale),
    }),
    generatedLabel,
    tagline: t("pdf.tagline"),
    disclaimer: t("pdf.cover.disclaimer"),
  });

  let y = PDF_MARGIN + 16;
  y = sectionTitle(doc, y, title, 40);
  const rows = opts.schedule.slice(0, 360).map((r) => [
    r.month,
    tableMoney(r.payment, opts.currency, opts.locale),
    tableMoney(r.interest, opts.currency, opts.locale),
    tableMoney(r.principal, opts.currency, opts.locale),
    tableMoney(r.balance, opts.currency, opts.locale),
  ]);
  addTable(
    doc,
    y,
    [
      t("pdf.col.month"),
      t("pdf.col.payment"),
      t("pdf.col.interest"),
      t("pdf.col.principal"),
      t("pdf.col.balance"),
    ],
    rows,
    {
      columnStyles: {
        0: {halign: "center" },
        1: {halign: "right" },
        2: {halign: "right" },
        3: {halign: "right" },
        4: {halign: "right" },
      },
    }
  );

  applyChrome(doc, {
    brand,
    periodLabel: title,
    generatedLabel,
    pageLabel: (n, total) => t("pdf.page", { n, total }),
  });
  downloadPdfBlob(
    doc,
    opts.filename ?? `BudgetOS-Amortization-${opts.debtName.replace(/\s+/g, "-")}.pdf`
  );
}
